/* eslint-disable react/static-property-placement */
import { EngineContext } from 'engine/components/EngineProvider';
import { AudioManager } from 'engine/lib/AudioManager';
import { V2 } from 'engine/lib/V2';
import { createStyles } from 'game/lib/createStyles';
import { easeOutQuart } from 'game/lib/easing';
import { PlayerSprite } from 'game/sprites/PlayerSprite';
import { PlayerState } from 'game/state/PlayerState';
import { colors, fontWeight, fonts } from 'game/styles/theme';
import clamp from 'lodash/clamp';
import React from 'react';
import { Message } from './Message';

const thud = require('game/sfx/thud.mp3');

const thudAudio = new AudioManager(thud);

const SIZE: V2 = [159 / 2, 230 / 2];

const classes = createStyles({
  deathScreen: {
    position: 'absolute',
    zIndex: 2,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: colors.white,
    opacity: 0,
  },
  svg: {
    position: 'relative',
    marginTop: -36,
    '& *': { transformBox: 'fill-box' },
  },

  gameOver: {
    position: 'absolute',
    top: 100,
    opacity: 0,
    fontFamily: fonts.nunito,
    textAlign: 'center',
  },
  gameOverHeading: {
    fontWeight: fontWeight.extraBold,
    textTransform: 'uppercase',
    fontSize: '3rem',
  },
  gameOverSub: {
  },
});

interface DeathScreenProps {
  reload: () => void,
  playerState: PlayerState,
}

export class DeathScreen extends React.Component<DeathScreenProps> {

  static contextType = EngineContext;
  context!: React.ContextType<typeof EngineContext>;

  start = 0;
  fadeDuration = 200;
  fallStart = 270;
  didThud = false;

  textStart = 320;
  textDuration = 100;

  constructor(props: DeathScreenProps, { time }: React.ContextType<typeof EngineContext>) {
    super(props);
    this.start = time.now;
  }

  onKeyDown = () => {
    const { reload } = this.props;
    const { time } = this.context;
    const delta = time.now - this.start;
    if (delta >= this.textStart) {
      reload();
    }
  };

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
  }

  componentDidUpdate() {
    if (this.didThud) return;
    const { time } = this.context;
    const delta = time.now - this.start;
    if (delta >= this.fallStart) {
      this.didThud = true;
      thudAudio.play();
    }
  }

  render() {
    const { playerState } = this.props;
    const { time, camera } = this.context;
    const delta = time.now - this.start;
    const fadeProgress = clamp(delta / this.fadeDuration, 1);
    const textProgress = clamp((delta - this.textStart) / this.textDuration, 0, 1);
    const [w, h] = SIZE;

    return (
      <div className={classes.deathScreen}>
        <div className={classes.overlay} style={{ opacity: fadeProgress }} />
        <svg
          className={classes.svg}
          style={delta >= this.fallStart ? {
            transform: `rotate(${playerState.facing === 'right' ? 90 : -90}deg)`,
            transformOrigin: `${playerState.facing === 'right' ? 70 : 30}% 95%`,
          } : undefined}
          width={w}
          height={h}
          viewBox={`0 0 ${w} ${h}`}
        >
          <g transform={`scale(${camera.scale})`}>
            <g
              transform={`scale(${playerState.facing === 'right' ? 1 : -1} 1)`}
              transform-origin="center"
            >
              {fadeProgress < 1 ? (
                PlayerSprite.stunned.frames[0]
              ) : (
                PlayerSprite.idle.frames[0]
              )}
            </g>
          </g>
        </svg>

        {textProgress > 0 && (
          <div
            className={classes.gameOver}
            style={{
              opacity: textProgress,
              transform: `translateY(${easeOutQuart(textProgress) * -20}px)`,
            }}
          >
            <div className={classes.gameOverHeading}>
              <Message text="Game Over" />
            </div>
            <div className={classes.gameOverSub}>
              <Message text="Press any key to restart" />
            </div>
          </div>
        )}
      </div>
    );
  }
}
